import { useEffect, useRef, useState } from 'react';

import { NpiSuggestionProgress } from './NpiSuggestionProgress';
import { useNpiSuggestions } from '../../hooks/useNpiSuggestions';
import {
  candidateDetail,
  candidateName,
  nameFieldDefault,
  normalizeStateCode,
} from '../../model/display';
import {
  stepAfterNameSearch,
  stepAfterRejectingSingle,
  stepFor,
  type PickerStep,
} from '../../model/steps';
import { NO_SUGGESTIONS, type NpiCandidate, type NpiSuggestionsRequest } from '../../model/types';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField } from '@/components/ui/input';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import {
  trackNpiSuggestionAccepted,
  trackNpiSuggestionsRejected,
  trackNpiSuggestionsSearched,
  trackNpiSuggestionsViewed,
} from '@/util/analyticsEvents';

export type NpiSuggestionPickerProps = {
  /** Off while the account is held for review or already has an NPI. */
  enabled: boolean;
  referralCode?: string;
  /** Opens the name search directly, as the "Search by name" link under the NPI field asks. */
  searchRequested?: boolean;
  /** The person confirmed a candidate. The screen fills the NPI and runs its usual lookup. */
  onSelect: (candidate: NpiCandidate) => void;
};

/**
 * Offers the registry records the person most likely is, for them to confirm,
 * so they need not know their NPI. The person always chooses: nothing is
 * selected for them, and "Not me" sits beside "Yes" at the same weight.
 *
 * Order of offers: the server's own guess from the account, then a search by
 * the name the person types, with a state if they add one. Typing the NPI
 * below stays available throughout.
 */
export function NpiSuggestionPicker({
  enabled,
  referralCode,
  searchRequested = false,
  onSelect,
}: NpiSuggestionPickerProps) {
  const [request, setRequest] = useState<NpiSuggestionsRequest>({ referralCode });

  const [step, setStep] = useState<PickerStep>('loading');

  const [nameInput, setNameInput] = useState('');

  const [stateInput, setStateInput] = useState('');

  /**
   * Counts submitted searches, so a search repeating the last one still leaves
   * the loading step: its query is already cached and nothing else changes.
   */
  const [searchCount, setSearchCount] = useState(0);

  const searchedName = request.name !== undefined;

  const state = useNpiSuggestions(
    { ...request, referralCode: request.referralCode ?? referralCode },
    { enabled }
  );

  const isLoading = state.phase === 'running';

  const suggestions = state.phase === 'done' ? state.suggestions : NO_SUGGESTIONS;

  const viewedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || isLoading) {
      return;
    }

    if (!searchedName) {
      setNameInput((current) => current || nameFieldDefault(suggestions.bestNameGuess));

      setStep(searchRequested ? 'name' : stepFor(suggestions));
    } else {
      setStep(stepAfterNameSearch(suggestions, request.state !== undefined));
    }

    const viewKey = `${request.name ?? ''}|${request.state ?? ''}`;

    if (viewedFor.current !== viewKey) {
      viewedFor.current = viewKey;

      trackNpiSuggestionsViewed({
        candidateCount: suggestions.candidates.length,
        confidence: suggestions.confidence,
        step: viewedStep(searchedName, request.state !== undefined),
      });
    }
  }, [
    enabled,
    isLoading,
    suggestions,
    searchedName,
    searchRequested,
    searchCount,
    request.name,
    request.state,
  ]);

  useEffect(() => {
    if (searchRequested && enabled) {
      setStep('name');
    }
  }, [searchRequested, enabled]);

  if (!enabled) {
    return null;
  }

  const select = (candidate: NpiCandidate, rank: number) => {
    trackNpiSuggestionAccepted({
      rank,
      candidateCount: suggestions.candidates.length,
      ipStateMatched: suggestions.ipState ? candidate.state === suggestions.ipState : null,
    });

    onSelect(candidate);
  };

  const reject = (next: PickerStep) => {
    trackNpiSuggestionsRejected({ candidateCount: suggestions.candidates.length });

    setStep(next);
  };

  const stateRequired = step === 'search';

  const typedState = normalizeStateCode(stateInput);

  const stateInvalid = stateInput.trim() !== '' && !typedState;

  const canSearch = nameInput.trim() !== '' && !stateInvalid && (!stateRequired || !!typedState);

  const searchByName = () => {
    const name = nameInput.trim();

    const state = typedState;

    if (!canSearch) {
      return;
    }

    trackNpiSuggestionsSearched({ withState: !!state });

    setStep('loading');

    setSearchCount((count) => count + 1);

    setRequest({ referralCode, name, ...(state ? { state } : {}) });
  };

  if (step === 'loading' || (isLoading && step !== 'name')) {
    return (
      <NpiSuggestionProgress
        stage={state.phase === 'running' ? state.stage : null}
        fromTypedName={searchedName}
      />
    );
  }

  if (step === 'confirm' && suggestions.candidates[0]) {
    const candidate = suggestions.candidates[0];

    return (
      <VStack className="w-full gap-3 rounded-md border border-outline-200 bg-background-50 p-4">
        <Text size="md" className="font-semibold text-typography-900">
          Is this you?
        </Text>
        <CandidateSummary candidate={candidate} />
        <HStack className="w-full flex-wrap gap-3">
          <Button size="md" onPress={() => select(candidate, 1)} className="min-w-[140px] flex-1">
            <ButtonText>Yes, that's me</ButtonText>
          </Button>
          <Button
            size="md"
            variant="outline"
            action="secondary"
            onPress={() => reject(stepAfterRejectingSingle(suggestions))}
            className="min-w-[140px] flex-1">
            <ButtonText className="text-typography-900">Not me</ButtonText>
          </Button>
        </HStack>
      </VStack>
    );
  }

  if (step === 'list') {
    const offered =
      suggestions.confidence === 'single'
        ? suggestions.candidates.slice(1)
        : suggestions.candidates;

    const rankOffset = suggestions.confidence === 'single' ? 2 : 1;

    return (
      <VStack className="w-full gap-2">
        <Text size="md" className="font-semibold text-typography-900">
          Are you one of these?
        </Text>
        {offered.map((candidate, index) => (
          <Pressable
            key={candidate.npi}
            onPress={() => select(candidate, index + rankOffset)}
            className="w-full rounded-md border border-outline-200 bg-background-0 p-3 data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100"
            {...getAccessibilityProps({
              label: `${candidateName(candidate)}, ${candidateDetail(candidate)}`,
              role: 'button',
              hint: 'Uses this NPI',
            })}>
            <CandidateSummary candidate={candidate} />
          </Pressable>
        ))}
        <Button
          size="md"
          variant="link"
          onPress={() => reject('name')}
          className="min-h-[48px] self-start">
          <ButtonText className="text-primary-500">None of these</ButtonText>
        </Button>
      </VStack>
    );
  }

  if (step === 'exhausted') {
    return (
      <VStack className="w-full gap-1">
        <Text size="sm" className="w-full text-typography-700">
          We couldn't find you in the NPI registry. Try another spelling, or enter your NPI below.
        </Text>
        <Button
          size="md"
          variant="link"
          onPress={() => setStep('name')}
          className="min-h-[48px] self-start">
          <ButtonText className="text-primary-500">Try a different name</ButtonText>
        </Button>
      </VStack>
    );
  }

  return (
    <VStack className="w-full gap-2">
      <Text size="sm" className="font-[500] text-typography-900">
        {stateRequired
          ? "Add the state you practice in and we'll look again."
          : 'Find your NPI by name, as it appears on your license. Adding your state helps.'}
      </Text>
      <HStack className="w-full gap-2">
        <Input className="flex-1">
          <InputField
            value={nameInput}
            onChangeText={setNameInput}
            placeholder="First and last name"
            autoCorrect={false}
            autoComplete="name"
            selectTextOnFocus
            clearButtonMode="while-editing"
            enterKeyHint="search"
            returnKeyType="search"
            onSubmitEditing={searchByName}
            {...getAccessibilityProps({ label: 'Your first and last name' })}
          />
        </Input>
        <Input className="w-[128px]" isInvalid={stateInvalid}>
          <InputField
            value={stateInput}
            onChangeText={setStateInput}
            placeholder="State"
            autoCapitalize="words"
            autoCorrect={false}
            autoComplete="postal-address-region"
            textContentType="addressState"
            enterKeyHint="search"
            returnKeyType="search"
            onSubmitEditing={searchByName}
            {...getAccessibilityProps({ label: 'State you practice in, like CO or Colorado' })}
          />
        </Input>
      </HStack>
      {stateInvalid ? (
        <Text size="xs" className="text-error-700" accessibilityLiveRegion="polite">
          Enter a US state, like CO or Colorado.
        </Text>
      ) : null}
      <Button
        size="md"
        onPress={searchByName}
        isDisabled={!canSearch}
        className="min-h-[48px] self-start px-6">
        <ButtonText>Search</ButtonText>
      </Button>
    </VStack>
  );
}

function viewedStep(searchedName: boolean, withState: boolean): 'initial' | 'name' | 'search' {
  if (!searchedName) {
    return 'initial';
  }

  return withState ? 'search' : 'name';
}

function CandidateSummary({ candidate }: { candidate: NpiCandidate }) {
  const detail = candidateDetail(candidate);

  return (
    <VStack className="w-full gap-0.5">
      <Text size="md" className="text-typography-900">
        {candidateName(candidate)}
      </Text>
      {detail ? (
        <Text size="sm" className="text-typography-700">
          {detail}
        </Text>
      ) : null}
      <Text size="xs" className="text-typography-600">
        NPI {candidate.npi}
      </Text>
    </VStack>
  );
}
